import _ from 'lodash';
import {
  ApiFilterOperation,
  FileRef,
  Filter,
  ListSource,
  Params,
  abortableFetch,
  useAutocompleteSource,
  useFetch,
} from '@eas/common-web';
import { WorkOrder, WorkOrderAutocomplete } from '@models';
import { EvidenceApiUrl } from '@enums';

export function useWorkOrders() {
  return useAutocompleteSource<WorkOrderAutocomplete>({
    url: `${EvidenceApiUrl.WORK_ORDERS}/autocomplete`,
  });
}

export function useWorkOrdersList(filters?: Filter[]) {
  return useFetch<ListSource<WorkOrder>>(`${EvidenceApiUrl.WORK_ORDERS}/list`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      size: -1,
      sort: [
        {
          field: 'workOrderId',
          order: 'ASC',
          type: 'FIELD',
          missing: 'LAST',
        },
      ],
      filters: [
        {
          operation: ApiFilterOperation.AND,
          filters: [
            {
              field: 'currentState',
              operation: ApiFilterOperation.NOT_NULL,
            },
            ...(filters ?? []),
          ],
        },
      ],
    }),
  });
}

export function useWorkOrdersAbortableList(api: string, params: Params) {
  return abortableFetch(api, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...params,
      filters: [
        {
          operation: ApiFilterOperation.AND,
          filters: [
            {
              field: 'currentState',
              operation: ApiFilterOperation.NOT_NULL,
            },
            ...(params.filters ?? []),
          ],
        },
      ],
    }),
  });
}

export function updateState(id: string, code: string) {
  const query = new URLSearchParams({
    code: code.toString(),
  });

  return abortableFetch(
    `${EvidenceApiUrl.WORK_ORDERS}/${id}/change/state?${query.toString()}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function updateWorkOrder(id: string, body: Partial<WorkOrder>) {
  return abortableFetch(`${EvidenceApiUrl.WORK_ORDERS}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function updateTravelTime(
  id: string,
  body: { travelPrevious: number; travelNext: number }
) {
  const query = new URLSearchParams({
    travelNext: body.travelNext.toString(),
    travelPrevious: body.travelPrevious.toString(),
  });

  return abortableFetch(
    `${EvidenceApiUrl.WORK_ORDERS}/${id}/update/travel?${query.toString()}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function planWorkOrder(
  id: string,
  body: {
    ownerId: string;
    ownerType: 'TECHNICIAN' | 'MOBILE_UNIT';
    plannedFrom: string;
    plannedTo: string;
    travelPrevious?: number;
    travelNext?: number;
  },
  isMultiday = false
) {
  return abortableFetch(
    `${EvidenceApiUrl.WORK_ORDERS}/${id}/plan${isMultiday ? '/multi-day' : ''}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(isMultiday ? _.omit(body, 'plannedTo') : body),
    }
  );
}

export function bulkPlanWorkOrder({
  ids,
  ownerId,
  ownerType,
  date,
}: {
  ids: string[];
  ownerId: string;
  ownerType: 'TECHNICIAN' | 'MOBILE_UNIT';
  date: string;
}) {
  return abortableFetch(`${EvidenceApiUrl.WORK_ORDERS}/group/plan`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workOrderIds: ids,
      ownerId,
      ownerType,
      date,
    }),
  });
}

export function completePlan(body: {
  calendarOwners: {
    id: string;
    type: 'TECHNICIAN' | 'MOBILE_UNIT';
  }[];
  from: string;
  to: string;
}) {
  return abortableFetch(`${EvidenceApiUrl.WORK_ORDERS}/plan/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function validateWorkOrder(
  id: string,
  body: {
    ownerId: string;
    ownerType: 'TECHNICIAN' | 'MOBILE_UNIT';
    plannedFrom: string;
    plannedTo: string;
  },
  isMultiDay?: boolean
) {
  return abortableFetch(
    `${EvidenceApiUrl.WORK_ORDERS}/${id}/plan/${
      isMultiDay ? 'multi-day/' : ''
    }validate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(isMultiDay ? _.omit(body, 'plannedTo') : body),
    }
  );
}

export function unplanWorkOrder(id: string) {
  return abortableFetch(`${EvidenceApiUrl.WORK_ORDERS}/${id}/unplan`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
  });
}

export function addAttachment(id: string, fileRefs: FileRef[]) {
  return abortableFetch(`${EvidenceApiUrl.WORK_ORDERS}/${id}/attachment`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fileRefs),
  });
}

export function removeAttachment(id: string, fileRef: FileRef) {
  return abortableFetch(`${EvidenceApiUrl.WORK_ORDERS}/${id}/attachment`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: fileRef.id }),
  });
}
/**
 * Moves WO in time.
 *
 * It has to be in POZ state.
 */
export function movePOZWorkOrder(id: string, newStart: string) {
  return abortableFetch(
    `${EvidenceApiUrl.WORK_ORDERS}/${id}/move?newStart=${newStart}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}
